import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageHero from '../components/layout/PageHero';
import { ChevronRight } from 'lucide-react';
import { searchApi } from '../lib/api';
import { SEARCH_TYPES, buildUnifiedGroups } from '../lib/searchRegistry';

const RESULTS_LIMIT = 50;

const ResultCard = ({ item }) => (
  <Link to={item.url} data-testid={`search-result-${item.id}`}>
    <Card className="technical-card h-full">
      <CardContent className="p-6 flex flex-col h-full">
        {item.category && (
          <Badge variant="outline" className="w-fit mb-3 font-mono text-xs uppercase tracking-wider">
            {item.category}
          </Badge>
        )}
        <h3 className="font-chivo text-lg font-bold mb-2 line-clamp-2">{item.title}</h3>
        {item.excerpt && (
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2 flex-1">{item.excerpt}</p>
        )}
        <div className="flex items-center justify-end pt-4 border-t border-structure mt-auto">
          <ChevronRight className="h-4 w-4 text-autoxium-blue" />
        </div>
      </CardContent>
    </Card>
  </Link>
);

const ComingSoonNotice = ({ label }) => (
  <div className="text-center py-16" data-testid="search-coming-soon">
    <Badge variant="outline" className="font-mono text-xs mb-4">Coming Soon</Badge>
    <p className="text-muted-foreground">{label} isn&rsquo;t available yet, so there&rsquo;s nothing to search here.</p>
  </div>
);

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim();
  const activeTab = searchParams.get('type') || 'all';

  const [backendGroups, setBackendGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query) {
      setBackendGroups([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    searchApi
      .search(query, RESULTS_LIMIT)
      .then((data) => {
        if (!cancelled) setBackendGroups(data.groups || []);
      })
      .catch(() => {
        if (!cancelled) setBackendGroups([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  // Live sections only (backend-fanned-out + client-catalog groups, per SEARCH_TYPES);
  // "soon" sections like Courses are handled separately below so their tab always
  // shows regardless of whether the query text happens to match the section name.
  const sections = useMemo(
    () => buildUnifiedGroups(query, backendGroups, RESULTS_LIMIT).filter((g) => !g.comingSoon),
    [query, backendGroups]
  );

  const totalCount = sections.reduce((sum, s) => sum + s.count, 0);

  const tabs = [
    { type: 'all', label: 'All', count: totalCount },
    ...SEARCH_TYPES.map((entry) => {
      if (entry.source === 'soon') return { type: entry.type, label: entry.label, count: 0, comingSoon: true };
      const section = sections.find((s) => s.type === entry.type);
      return { type: entry.type, label: entry.label, count: section ? section.count : 0 };
    }),
  ];

  const handleTabChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('type');
    else next.set('type', value);
    setSearchParams(next, { replace: true });
  };

  const comingSoonTab = tabs.find((t) => t.type === activeTab && t.comingSoon);
  const visibleSections = activeTab === 'all' ? sections : sections.filter((s) => s.type === activeTab);

  return (
    <div className="min-h-screen" data-testid="search-page">
      <PageHero
        eyebrow="Search"
        title={query ? `Results for "${query}"` : 'Search AUTOXIUM'}
        description={
          query
            ? `${totalCount} result${totalCount !== 1 ? 's' : ''} found`
            : 'Search articles, tools, simulations, how it works topics, and community discussions.'
        }
        testId="search-hero"
      />

      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-10">
            <TabsList className="flex-wrap h-auto bg-secondary/40 p-1 gap-1">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.type}
                  value={tab.type}
                  className="font-mono text-xs uppercase tracking-wider"
                  data-testid={`search-tab-${tab.type}`}
                >
                  {tab.label} ({tab.count})
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {!query && (
            <div className="text-center py-16 text-muted-foreground" data-testid="search-no-query">
              Start typing in the search bar above to find articles, tools, and more.
            </div>
          )}

          {query && loading && (
            <div className="text-center py-16 text-muted-foreground">Searching…</div>
          )}

          {query && !loading && comingSoonTab && <ComingSoonNotice label={comingSoonTab.label} />}

          {query && !loading && !comingSoonTab && totalCount === 0 && (
            <div className="text-center py-16" data-testid="search-empty-state">
              <p className="text-lg font-medium mb-2">No results for &lsquo;{query}&rsquo;.</p>
              <p className="text-muted-foreground">Try a different term, or browse the sections below.</p>
            </div>
          )}

          {query &&
            !loading &&
            !comingSoonTab &&
            visibleSections.map((section) => {
              if (section.items.length === 0) return null;
              return (
                <div key={section.type} className="mb-12" data-testid={`search-section-${section.type}`}>
                  {activeTab === 'all' && (
                    <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight mb-6">
                      {section.label}
                    </h2>
                  )}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {section.items.map((item) => (
                      <ResultCard key={`${section.type}-${item.id}`} item={item} />
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      </section>
    </div>
  );
};

export default SearchPage;
